/**
 * The one place that decides what pressing a payment button does.
 *
 * Four screens start a Stripe checkout - the public plan block on Home, the player's Account
 * Settings, the single-auction dialog on AuctionPlayer, and the resume-after-sign-in on Login.
 * They each used to re-implement the refusal check, and three of the four did nothing at all
 * when the call failed. The branching lives here so it cannot drift apart again; the callers
 * only choose how to *show* the outcome.
 */

export const CHECKOUT_OUTCOME = {
    /** A session URL came back and this tab is navigating to Stripe. */
    REDIRECTING: 'redirecting',
    /** HTTP 200 with status:false - a business rule, carrying the server's own wording. */
    REFUSED: 'refused',
    /** No session to go to: the call failed, or answered without a session URL. */
    UNAVAILABLE: 'unavailable',
};

const DEFAULT_UNAVAILABLE_MESSAGE = 'We could not open the payment page. Please try again.';

/**
 * Normalises whatever auth-service.js handed back into a single API envelope.
 *
 * Its wrappers never throw - every one of them ends `catch (err) { return err?.response?.data }` -
 * so the envelope arrives in three different shapes: under `res.data` for any reply that came
 * back as an axios response, as `res` itself when a 4xx/5xx body was unwrapped by that catch,
 * and as `undefined` when there was no reply at all. Reading only `res?.data?.status`, which is
 * what every call site did, misses the last two - which is why an offline browser and a refusal
 * sent with a 4xx both produced a button that did nothing whatsoever.
 *
 * The `typeof res.status === 'boolean'` test has to come first: that is true only for the
 * unwrapped case, because an axios response carries an HTTP status NUMBER in the same place.
 *
 * @param {*} res Whatever the auth-service wrapper returned.
 * @returns {object|null} The API envelope, or null when the server never answered.
 */
export const readApiBody = (res) => {
    if (!res || typeof res !== 'object') return null;
    if (typeof res.status === 'boolean') return res;
    if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
    return null;
};

/**
 * Starts a checkout and, when there is somewhere to go, goes there.
 *
 * Same-tab navigation is deliberate. A window opened after an await has lost the user activation
 * the click created, so browsers block it - that is WEB-P1-115 - and an opener tab could not
 * observe the outcome anyway. The session URL is returned as well, so the caller can leave a
 * link on screen that the user can click themselves.
 *
 * Deliberately does NOT toast: apiClient.js already reports timeouts, an unreachable server and
 * 5xx, and a second toast here would double up. Callers render their own inline state.
 *
 * Before leaving, it remembers the route to come back to if the user presses Back on the Stripe
 * form - see saveCheckoutReturn below for why the cancel URL itself cannot carry it.
 *
 * @param {() => Promise<*>} createSession Zero-argument call to the auth-service wrapper, e.g.
 *        `() => CreatePymenentUrlFn(planId)` or `() => CreateAuctionPassSession(auctionId)`.
 * @param {{returnTo?: string, returnOnSuccess?: boolean}} [options] `returnTo` is the app route
 *        /PaymentCancel sends the user back to. Defaults to the current route, which is right for
 *        every caller that starts the checkout on the page the user picked it from.
 *        `returnOnSuccess` sends /PaymentSuccess back there too, once the payment is confirmed
 *        (#238 AC8: a pass bought on Auction Details returns to it); without it success goes to
 *        the dashboard, as it always has.
 * @returns {Promise<{outcome: string, message: string, sessionUrl: string|null, notified: boolean}>}
 *          `notified` is true when apiClient.js has already told the user why (no reply, a
 *          timeout, a 5xx), so a caller that answers with a toast knows not to fire a second one.
 */
export const startCheckout = async (createSession, { returnTo, returnOnSuccess = false } = {}) => {
    const body = readApiBody(await createSession());
    const sessionUrl = body?.data?.[0]?.sessionUrl || null;

    // The server owns this rule and its wording, including the date the current period ends.
    if (body?.status === false) {
        return {
            outcome: CHECKOUT_OUTCOME.REFUSED,
            message: body.message || DEFAULT_UNAVAILABLE_MESSAGE,
            sessionUrl: null,
            notified: false,
        };
    }

    if (!sessionUrl) {
        return {
            outcome: CHECKOUT_OUTCOME.UNAVAILABLE,
            message: body?.message || DEFAULT_UNAVAILABLE_MESSAGE,
            sessionUrl: null,
            notified: body === null,
        };
    }

    saveCheckoutReturn(returnTo ?? window.location.pathname + window.location.search, { onSuccess: returnOnSuccess });
    window.location.assign(sessionUrl);

    return {
        outcome: CHECKOUT_OUTCOME.REDIRECTING,
        message: '',
        sessionUrl,
        notified: false,
    };
};

/* ------------------------------------------------------------------------------------------
 * The purchase a signed-out visitor walked in with.
 *
 * Previously a bare localStorage.setItem("checkoutValue", "1") on Home, read and erased inside
 * Login's submit handler and blind-deleted in two logout routines. Centralised here for the
 * same reason SESSION_ENDED_KEY lives in helper.js: a cross-page handoff needs one owner, or
 * the clearing drifts away from the writing - which is exactly how WEB-P1-248 loses a purchase.
 * ---------------------------------------------------------------------------------------- */

/** Kept as 'checkoutValue': the key is already sitting in returning users' browsers. */
const CHECKOUT_INTENT_KEY = 'checkoutValue';

/** A plan someone chose and walked away from must not hijack their next sign-in, hours later. */
const INTENT_MAX_AGE_MS = 30 * 60 * 1000;

const PLAN_LABELS = { 1: '$4.99 Monthly', 2: '$49.99 Yearly' };

/**
 * Names a plan for a sentence shown to the user.
 *
 * @param {string|number} planId The subscription plan id.
 * @returns {string} The plan's label, or a neutral word when the id is not one we know.
 */
export const describePlan = (planId) => PLAN_LABELS[String(planId)] || 'subscription';

/**
 * Remembers the plan a signed-out visitor chose, so sign-in can pick the purchase back up.
 *
 * @param {string|number} planId The subscription plan id.
 */
export const saveCheckoutIntent = (planId) => {
    if (!planId) return;
    localStorage.setItem(
        CHECKOUT_INTENT_KEY,
        JSON.stringify({ planId: String(planId), savedAt: Date.now() })
    );
};

/** Forgets the pending purchase. */
export const clearCheckoutIntent = () => localStorage.removeItem(CHECKOUT_INTENT_KEY);

/**
 * Reads the pending purchase, if there is a usable one.
 *
 * Tolerates the format shipped before this change, which stored the bare plan id, because the
 * value is already in browsers that will arrive on the new build.
 *
 * @returns {{planId: string}|null} The pending purchase, or null when there is none left.
 */
export const readCheckoutIntent = () => {
    const raw = localStorage.getItem(CHECKOUT_INTENT_KEY);
    if (!raw) return null;

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        parsed = null;
    }

    const intent =
        parsed && typeof parsed === 'object' ? parsed : { planId: String(parsed ?? raw) };

    if (!intent.planId || intent.planId === 'undefined' || intent.planId === 'null') {
        clearCheckoutIntent();
        return null;
    }

    if (intent.savedAt && Date.now() - intent.savedAt > INTENT_MAX_AGE_MS) {
        clearCheckoutIntent();
        return null;
    }

    return { planId: String(intent.planId) };
};

/* ------------------------------------------------------------------------------------------
 * Where to go when the user presses Back on the Stripe form.
 *
 * Stripe sends Back to the session's cancel_url, and the server sets one fixed /paymentCancel for
 * every checkout. It cannot vary by page: a resumed attempt hands back a session created with an
 * earlier cancel_url, which Stripe cannot change; a different URL under the same idempotency key
 * is a permanent 400; and the Android WebView exact-matches /paymentCancel. So the page that
 * starts the checkout remembers the route here and /paymentCancel only redirects to it.
 *
 * sessionStorage, not localStorage: it is per tab, which is exactly the scope of a same-tab trip
 * to Stripe and back, and it cannot leak into a checkout started in another tab.
 * ---------------------------------------------------------------------------------------- */

const CHECKOUT_RETURN_KEY = 'checkoutReturnTo';

/**
 * #238 AC8: set beside CHECKOUT_RETURN_KEY when the checkout's success should come back to the
 * same route (an auction pass), so /PaymentSuccess knows the route is wanted after a payment too.
 */
const CHECKOUT_RETURN_ON_SUCCESS_KEY = 'checkoutReturnOnSuccess';

/** Only an in-app path - never "//host", which the browser would read as another origin. */
const isAppPath = (path) =>
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

/**
 * Remembers the route a checkout was started from.
 *
 * @param {string} path An app route such as '/Player/Account_settings'.
 * @param {{onSuccess?: boolean}} [options] `onSuccess` marks the route as wanted after a confirmed
 *        payment as well (readCheckoutSuccessReturn); otherwise only Back on the Stripe form uses it.
 */
export const saveCheckoutReturn = (path, { onSuccess = false } = {}) => {
    if (!isAppPath(path)) return;
    try {
        sessionStorage.setItem(CHECKOUT_RETURN_KEY, path);
        if (onSuccess) sessionStorage.setItem(CHECKOUT_RETURN_ON_SUCCESS_KEY, '1');
        else sessionStorage.removeItem(CHECKOUT_RETURN_ON_SUCCESS_KEY);
    } catch (e) {
        // Storage blocked: /PaymentCancel falls back to a default route.
    }
};

/** Forgets the remembered route, for Back and for success alike. */
export const clearCheckoutReturn = () => {
    try {
        sessionStorage.removeItem(CHECKOUT_RETURN_KEY);
        sessionStorage.removeItem(CHECKOUT_RETURN_ON_SUCCESS_KEY);
    } catch (e) {
        // Nothing to clear when storage is unavailable.
    }
};

/**
 * The route a confirmed payment goes back to (#238 AC8), without forgetting it: /PaymentSuccess
 * reads it while rendering and clears it in its effect.
 *
 * @returns {string|null} The route, or null when the checkout did not ask for it (a subscription).
 */
export const readCheckoutSuccessReturn = () => {
    try {
        if (sessionStorage.getItem(CHECKOUT_RETURN_ON_SUCCESS_KEY) !== '1') return null;
        const path = sessionStorage.getItem(CHECKOUT_RETURN_KEY);
        return isAppPath(path) ? path : null;
    } catch (e) {
        return null;
    }
};

/**
 * Reads and forgets the remembered route, so it is used at most once.
 *
 * @returns {string|null} The route to return to, or null when there is none.
 */
export const takeCheckoutReturn = () => {
    let path = null;
    try {
        path = sessionStorage.getItem(CHECKOUT_RETURN_KEY);
    } catch (e) {
        return null;
    }
    clearCheckoutReturn();
    return isAppPath(path) ? path : null;
};
